<template>
  <div
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      { [$class('loading')]: !product },
    ]"
  >
    <a
      class="product-card__link"
      :href="selectedVariantUrl"
    >
      <responsive-image
        :class="$blockClass(blockClass, 'product-card-image')"
        block-class="product-card"
        :image="selectedVariantImage"
        min-max="300-820"
        ratio="4:5"
        :sizes="sizes"
      />

      <responsive-image
        v-if="altVariantImageUrl"
        :class="$blockClass(blockClass, 'product-card-image-alt')"
        block-class="product-card-alt"
        :image="{src: altVariantImageUrl}"
        min-max="300-820"
        ratio="4:5"
        :sizes="sizes"
      />

      <div
        class="product-card__information"
        :class="[$blockClass(blockClass, 'product-card-information'), {'product-card__information--with-alt-image': altVariantImageUrl}]"
      >
        <div
          class="product-card__content"
          :class="$blockClass(blockClass, 'product-card-content')"
        >
          <p
            class="product-card__title"
            :class="$blockClass(blockClass, 'product-card-title')"
            v-text="product.title"
          />

          <p
            class="product-card__prices"
            :class="$blockClass(blockClass, 'product-card-prices')"
            v-text="formattedPrice"
          />
        </div>

        <product-badge
          v-if="enableBadges"
          :class="$blockClass(blockClass, 'product-card-badge')"
          :product="product"
          block-class="product-card"
        />
      </div>
    </a>

    <product-swatches
      v-if="enableSwatches && colorOptions"
      product-card
      :option="colorOptions"
      :options="options"
      :product="product"
      block-class="product-swatches--location-product-card"
      :swatch-behavior="swatchBehavior"
      is-collection
      @option-change="updateVariant"
    />
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} index - Product card's forloop index.
 * @param {Boolean} enableBadges - Displays badges on product card.
 * @param {Boolean} enableSwatches - Displays swatches on product card.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {String} sizes - Responsive image sizes.
 *
 */
import ResponsiveImage from '~global/images/responsive-image'
import ProductSwatches from '~async/product-swatches/product-swatches'
import ProductBadge from '~async/product-badge/product-badge'

export default {
  name: 'ProductCard',

  components: {
    ProductBadge,
    ProductSwatches,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
    enableBadges: {
      type: Boolean,
      default: true,
    },
    enableSwatches: {
      type: Boolean,
      default: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    collectionUrl: {
      type: String,
      default: '',
    },
    sizes: {
      type: [String, null],
      default: null,
    },
    swatchBehavior: {
      type: Boolean,
      default: false,
    },

  },
  data() {
    return {
      selectedVariantImage: this.product.featuredImage, // Assuming featuredImage is the initial product image
      altVariantImageUrl: null,
      selectedVariant: this.product ? this.product.variants[0] : null, // Assuming variant is the initial product variant
      selectedVariantUrl: this.product.url,  // Assuming url is the initial product url
    }
  },

  computed: {
    currentPrice() {
      return this.selectedVariant ? this.selectedVariant.price : this.product.price
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (!this.currentPrice) {
        return ''
      } else if (this.currentPrice.min == this.currentPrice.max) {
        return this.$formatMoney(this.currentPrice.amount)
      } else {
        return (
          this.$string('product.prices.from') +
          ' ' +
          this.$formatMoney(this.currentPrice.amount)
        )
      }
    },

    /**
     * Computed color options.
     * @returns {Object}
     */
    colorOptions() {
      if (!this.product) {
        return {}
      }

      return this.options.find((option) => {
        return option.name.toLowerCase() === this.$variable('productOptionsName.colorOption').toLowerCase()
      })
    },

    /**
     * Computed options.
     * - Fallback to liquid object.
     * @returns {Array}
     */
    options() {
      if (!this.product) {
        return []
      }

      const rawHiddenVariantIdList = this.product.metafields ?
        this.product.metafields
          .find(metafield => metafield.namespace === 'custom' && metafield.key === 'hidden_variants')?.value
        : this.product.hiddenVariants ? this.product.hiddenVariants.value : null

      if(!rawHiddenVariantIdList) {
        return this.product.options
      }

      const hiddenVariantIdList = JSON.parse(rawHiddenVariantIdList)
        .map(globalVariantId => globalVariantId.split('/ProductVariant/')[1])
        .map(Number)

      const visibleVariants = this.product.variants.filter(variant => !hiddenVariantIdList.includes(variant.id))

      const filteredAllOptions = this.product.options.map((option, optionIndex) => {
        return {
          ...option,
          values: option.values.map(optionValue => {
            if (optionIndex === 0) {
              const visibleVariantsWithOptionValue = visibleVariants.filter(variant => {
                return variant.selectedOptions[optionIndex].value === optionValue
              })

              return visibleVariantsWithOptionValue.length > 0 ? optionValue : null
            }

            return optionValue
          }).filter(Boolean),
        }
      })

      return filteredAllOptions
    },

    /**
     * Compute the collection url to get product url with collection alias.
     * @returns {String}
     */
    productUrl() {
      let url = `/products/${this.product.handle}`

      if (this.collectionUrl) {
        url = this.collectionUrl + url
      }

      return url
    },
  },

  methods: {

  /**
   * Updates the image and variant information based on the selected color.
   * Sets the selected color image and variant, and updates the URL with the variant ID if available.
   * @param {Object} param - The parameter object.
   * @param {String} param.variantImage - The image URL of the selected variant.
   * @param {Object} param.variant - The selected variant object.
   */
    updateVariant({ variantImage, variant }) {
      console.log('updateVariant', variantImage, variant)
      this.selectedVariantImage = variantImage
      this.altVariantImageUrl = variant.lifestyleAssetUrl
      this.selectedVariant = variant
      const varUrl = this.selectedVariant?.id
        ? `${this.product.url}?variant=${this.selectedVariant.id}`
        : `${this.product.url}`
      this.selectedVariantUrl = varUrl
    },
  },
}
</script>

<style lang="scss">
@import "./product-card";
</style>
